import { colors } from 'app-v2/design-systems/theme/colors'
import { COLOR } from 'app-v2/design-tokens'
import { Arrow, Crown, Lock } from 'app/assets/icons'
import { ButtonMedium, ResponsiveContainer, media } from 'app/assets/styles'
import { Link } from 'app/common/link'
import { Loader } from 'app/common/loader'
import { H3, H4, H5, Text } from 'app/common/typography'
import { ErrorBanner, Layout } from 'app/ecommerce/common'
import { ButtonFooter, CartItem } from 'app/ecommerce/common/components'
import _ from 'lodash'
import React, { Component } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { colors as oldColors } from 'shared/assets/styles'
import type { ItemInfos, PurchasableItem } from 'shared/store/ecommerce/order'
import type { ECommerceMetadata } from 'shared/store/metadata'
import type {
    ClickAndCollectPromotion,
    MetaShortDTO,
    OperationDevice,
    Restaurant
} from 'shared/types'
import { PaymentProvider, catalogType, formatPrice } from 'shared/utils'
import { cartPriceIsLowerThanPromotionThreshold } from 'shared/utils/ecommerce'
import styled from 'styled-components'

const { grey, red2 } = oldColors

type Props = {
    products: Array<PurchasableItem>,
    pointsPerEuro: number,
    sortedProducts: Array<PurchasableItem>,
    priceOriginal: number,
    priceTotal: number,
    hasKingdom?: boolean,
    authenticated: boolean,
    pointsTotal: number,
    intl: IntlShape,
    operationsRepeat?: Array<OperationDevice>,
    cartHasPromotions: boolean,
    idsCouponsTemporaryBurned: Array<string>,
    minimumPrice?: number,
    maximumPrice?: number,
    removePromotionFromCart: (couponCode: string) => void,
    addProductToCart: (product: PurchasableItem) => void,
    setCurrentItem: (item: PurchasableItem) => void,
    setCurrentItemInfos: (productInfos: ItemInfos) => void,
    fetchCartPriceEdges: () => void,
    handleMissingProducts: () => void,
    promotions?: Array<ClickAndCollectPromotion>,
    removeProductFromCart: (product: PurchasableItem) => void,
    error?: {
        message: string,
        reason?: string
    },
    restaurant: Restaurant,
    fetchAvailableFeatures: () => void,
    navigate: (to: string) => void,
    sendECommerceMeta: ECommerceMetadata => void,
    pageMetadata: (payload: MetaShortDTO) => void
}

type State = {
    error?: {
        message: string,
        reason?: string,
        messageParam?: any
    }
}

class CartSummary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        const { error } = props
        this.state = {
            error: error && { ...error }
        }
    }
    componentDidMount() {
        const {
            fetchAvailableFeatures,
            fetchCartPriceEdges,
            pageMetadata,
            sendECommerceMeta,
            products
        } = this.props
        fetchAvailableFeatures()
        fetchCartPriceEdges()
        pageMetadata({})
        sendECommerceMeta({
            type: 'checkout',
            data: {
                step: 1,
                products
            }
        })
    }

    componentWillUnmount() {
        const { handleMissingProducts } = this.props
        handleMissingProducts()
    }

    sendBeginCheckoutMeta = () => {
        const { sendECommerceMeta, products } = this.props
        sendECommerceMeta({
            type: 'begin_checkout',
            data: { products }
        })
    }

    handleSubmit = () => {
        const {
            priceTotal,
            products,
            promotions,
            minimumPrice,
            maximumPrice,
            navigate,
            intl
        } = this.props
        this.sendBeginCheckoutMeta()
        if (priceTotal > 0 && minimumPrice && priceTotal < minimumPrice) {
            this.setState({
                error: {
                    message: 'clickandcollect.cart.error.minimum',
                    messageParam: minimumPrice
                }
            })
        } else if (
            priceTotal > 0 &&
            maximumPrice &&
            priceTotal >= maximumPrice
        ) {
            this.setState({
                error: {
                    message: 'clickandcollect.cart.error.maximum',
                    messageParam: maximumPrice
                }
            })
        } else {
            const {
                cartPriceTooLow,
                thresholds
            } = cartPriceIsLowerThanPromotionThreshold(
                priceTotal,
                products,
                promotions
            )
            if (cartPriceTooLow) {
                this.setState({
                    error: {
                        message: 'ecommerce.offer.error.threshold',
                        messageParam: thresholds[0]
                    }
                })
            } else {
                navigate(
                    intl.formatMessage({
                        id: 'routes.clickandcollect.cart.user.infos'
                    })
                )
            }
        }
    }

    render() {
        const {
            addProductToCart,
            sortedProducts,
            priceOriginal,
            priceTotal,
            hasKingdom,
            authenticated,
            pointsTotal,
            intl,
            restaurant,
            operationsRepeat,
            cartHasPromotions,
            idsCouponsTemporaryBurned,
            setCurrentItem,
            setCurrentItemInfos,
            promotions,
            removePromotionFromCart,
            removeProductFromCart,
            loader
        } = this.props

        const { error } = this.state
        const empty = _.isEmpty(sortedProducts)

        const renderEmptyCart = () => {
            return (
                <FormattedMessage id="clickandcollect.cart.empty">
                    {(text: string) => (
                        <StyledText color={grey} center>
                            {text}
                        </StyledText>
                    )}
                </FormattedMessage>
            )
        }

        const renderCategories = () => {
            return Object.entries(sortedProducts).map(
                (
                    [
                        categoryName: string,
                        purchasableItems: Array<PurchasableItem>
                    ],
                    index
                ) => {
                    return (
                        <CategoryContainer key={index}>
                            <StyledH3>{categoryName}</StyledH3>
                            {/*//$FlowFixMe*/}
                            {purchasableItems.map((item, index) => {
                                let isAValidGame
                                const { id } = item
                                if (item.type === catalogType.GAME) {
                                    const currentOperationMarketing =
                                        operationsRepeat &&
                                        operationsRepeat.find(
                                            o => o.code === item.operationCode
                                        )
                                    isAValidGame =
                                        !cartHasPromotions &&
                                        currentOperationMarketing &&
                                        currentOperationMarketing.coupons &&
                                        Boolean(
                                            currentOperationMarketing.coupons.find(
                                                c =>
                                                    !idsCouponsTemporaryBurned.includes(
                                                        c
                                                    )
                                            )
                                        )
                                }
                                let maxQuantityReached = false
                                /*//$FlowFixMe*/
                                const cartItems = purchasableItems.filter(
                                    item => item.id === id
                                )
                                const itemQuantity = cartItems.reduce(
                                    (itemQuantity, item) => {
                                        return itemQuantity + item.quantity
                                    },
                                    0
                                )
                                if (
                                    cartItems[0] &&
                                    cartItems[0].maxQuantity === itemQuantity
                                ) {
                                    maxQuantityReached = true
                                }
                                return (
                                    <Item key={index}>
                                        <CartItem
                                            product={item}
                                            isAValidGame={isAValidGame}
                                            deleteProduct={(
                                                product: PurchasableItem
                                            ) => removeProductFromCart(product)}
                                            addProduct={addProductToCart}
                                            setCurrentItem={setCurrentItem}
                                            setCurrentItemInfos={
                                                setCurrentItemInfos
                                            }
                                            maxQuantityReached={
                                                maxQuantityReached
                                            }
                                        />
                                    </Item>
                                )
                            })}
                        </CategoryContainer>
                    )
                }
            )
        }

        const renderKingdomPush = () => {
            const { pointsPerEuro, intl } = this.props
            const crownNumberMessageKey = (pointsPerEuro: number) => {
                switch (pointsPerEuro) {
                    case 4:
                        return 'clickandcollect.catalog.crown.number.double'
                    case 6:
                        return 'clickandcollect.catalog.crown.number.triple'
                    case 8:
                        return 'clickandcollect.catalog.crown.number.quadruple'
                    default:
                        return null
                }
            }
            const crownNumberMessage =
                crownNumberMessageKey(pointsPerEuro) &&
                intl.formatMessage({
                    id: crownNumberMessageKey(pointsPerEuro)
                })
            return (
                <KingdomPushContainer>
                    {pointsTotal > 0 && hasKingdom && (
                        <KingdomPush noPadding={!authenticated}>
                            {kingdomPush()}
                        </KingdomPush>
                    )}
                    {crownNumberMessage && (
                        <FormattedMessage
                            id="clickandcollect.catalog.crown.number"
                            values={{
                                quantity: crownNumberMessage
                            }}>
                            {(text: string) => (
                                <StyledMoreCrownsText center>
                                    {text}
                                </StyledMoreCrownsText>
                            )}
                        </FormattedMessage>
                    )}
                </KingdomPushContainer>
            )
        }

        const kingdomPush = () => {
            const key = authenticated ? 'connected' : 'not.connected'

            return (
                <StyledPushConnected>
                    {authenticated && (
                        <FormattedMessage id="clickandcollect.cart.congrats">
                            {(text: string) => (
                                <H4 color={COLOR.SECONDARY} center>
                                    {text}
                                </H4>
                            )}
                        </FormattedMessage>
                    )}
                    <Points>
                        <FormattedMessage
                            id={`clickandcollect.cart.points.${key}`}>
                            {(text: string) => (
                                <Text color={COLOR.SECONDARY} center>
                                    {text}
                                </Text>
                            )}
                        </FormattedMessage>
                        <PointsNumber color={COLOR.SECONDARY}>
                            {pointsTotal.toString()}
                        </PointsNumber>
                        <Crown color={colors.meltyYellow} size={30} />
                    </Points>
                </StyledPushConnected>
            )
        }

        return (
            <Layout
                border
                cart
                currentStep={2}
                background="plain"
                backgroundColor={colors.lightMayo}>
                {!!error && (
                    <ErrorBanner
                        isCart
                        text={
                            error.message &&
                            intl.formatMessage(
                                {
                                    id: error.message
                                },
                                {
                                    value: error.messageParam
                                }
                            )
                        }
                        description={
                            error.reason &&
                            intl.formatMessage({
                                id: error.reason
                            })
                        }
                        onClose={() =>
                            this.setState({
                                error: undefined
                            })
                        }
                    />
                )}

                <StyledContainer>
                    <Loader
                        loading={loader.isLoading}
                        size="large"
                        color={COLOR.PRIMARY}
                        loaderMessage={loader.intlKeyMessage}
                        children={
                            <>
                                <StyledLink
                                    url={intl.formatMessage({
                                        id: 'routes.clickandcollect.catalog'
                                    })}>
                                    <StyledButtonMedium color={COLOR.PRIMARY}>
                                        <ButtonContainer>
                                            <Arrow color={COLOR.PRIMARY} />
                                            <FormattedMessage id="clickandcollect.cart.return.catalog">
                                                {(text: string) => (
                                                    <StyledH5
                                                        color={COLOR.PRIMARY}>
                                                        {text}
                                                    </StyledH5>
                                                )}
                                            </FormattedMessage>
                                        </ButtonContainer>
                                        <Underline />
                                    </StyledButtonMedium>
                                </StyledLink>
                                {empty ? (
                                    renderEmptyCart()
                                ) : (
                                    <div>
                                        {renderCategories()}
                                        {renderKingdomPush()}
                                    </div>
                                )}
                                <PriceContainer>
                                    <FormattedMessage id="clickandcollect.cart.subtotal">
                                        {(text: string) => <Text>{text}</Text>}
                                    </FormattedMessage>
                                    <Text>{formatPrice(priceOriginal)}</Text>
                                </PriceContainer>
                                <Line />
                                {promotions &&
                                    promotions.length > 0 &&
                                    promotions.map((promotion, index) => (
                                        <PromotionContainer key={index}>
                                            <Promotion>
                                                <Text>
                                                    {promotion.promotionName}
                                                </Text>
                                                {promotion.couponCode && (
                                                    <DeletePromotion
                                                        onClick={() =>
                                                            removePromotionFromCart(
                                                                promotion.couponCode ||
                                                                    ''
                                                            )
                                                        }>
                                                        <FormattedMessage id="clickandcollect.discounts.remove">
                                                            {(text: string) => (
                                                                <RemovePromotion>
                                                                    {text}
                                                                </RemovePromotion>
                                                            )}
                                                        </FormattedMessage>
                                                    </DeletePromotion>
                                                )}
                                            </Promotion>
                                            <Text>
                                                {formatPrice(
                                                    priceTotal - priceOriginal
                                                )}
                                            </Text>
                                        </PromotionContainer>
                                    ))}
                                <Line />
                                <PriceContainer>
                                    <FormattedMessage id="clickandcollect.cart.total">
                                        {(text: string) => <H3>{text}</H3>}
                                    </FormattedMessage>
                                    <H3>{formatPrice(priceTotal)}</H3>
                                </PriceContainer>
                                <Payment>
                                    <Lock />
                                    <FormattedMessage id="clickandcollect.payment.secured">
                                        {(text: string) => <Text>{text}</Text>}
                                    </FormattedMessage>
                                </Payment>
                            </>
                        }
                    />

                    <ButtonFooter
                        shouldStick
                        showTermsOfSales
                        labelKey={
                            (priceTotal && priceTotal > 0) ||
                            (!priceTotal && !empty)
                                ? 'clickandcollect.validate.cart.footer'
                                : 'clickandcollect.validate.cart.footer.no.price'
                        }
                        labelParams={{
                            finalPrice:
                                !empty && !priceTotal
                                    ? formatPrice(0)
                                    : formatPrice(priceTotal)
                        }}
                        onClick={this.handleSubmit}
                        disabled={empty || loader.isLoading}
                        isAdyen={
                            restaurant?.paymentServiceProvider ===
                            PaymentProvider.ADYEN
                        }
                    />
                </StyledContainer>
            </Layout>
        )
    }
}

const StyledContainer = styled(ResponsiveContainer)`
    margin: 60px auto 90px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    ${media.medium`
        margin-top: 150px;
        max-width: 1000px;
    `};
`

const StyledLink = styled(Link)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
`

const StyledButtonMedium = styled(ButtonMedium)`
    display: flex;
    flex-direction: column;
    align-items: center;
    background: transparent;
    border: none;
`

const ButtonContainer = styled.div`
    display: flex;
    align-items: center;
`

const StyledH5 = styled(H5)`
    margin-left: 10px;
    ${media.medium`
        margin-left: 20px;
    `};
`

const Underline = styled.div`
    width: 100%;
    height: 2px;
    background: ${COLOR.PRIMARY};
`

const StyledText = styled(Text)`
    margin: 80px 0;
    display: flex;
    justify-content: center;
`

const CategoryContainer = styled.div`
    margin: auto;
    padding: 20px 0px;
`

const StyledH3 = styled(H3)`
    margin-bottom: -10px;
`

const Item = styled.div`
    margin: 20px 0;
`

const KingdomPushContainer = styled.div`
    min-height: 30px;
    margin: auto;
`

const KingdomPush = styled.div`
    border-radius: 10px;
    overflow: hidden;
    margin: 20px auto 28px;
    ${props => props.noPadding && 'padding-bottom: 0;'};
    background-size: cover;
    background: ${COLOR.PRIMARY};
    max-width: 600px;
`

const StyledMoreCrownsText = styled(Text)`
    display: flex;
    justify-content: center;
    margin: 0px auto 50px;
`

const StyledPushConnected = styled.div`
    padding: 20px;
`

const Points = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: flex-end;
`

const PointsNumber = styled(H5)`
    margin-left: 10px;
`

const PriceContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 10px 0;
`

const Line = styled.div`
    height: 0.5px;
    background: ${COLOR.PRIMARY};
`

const PromotionContainer = styled.div`
    margin: 10px 0px;
    flex-direction: row;
    display: flex;
    justify-content: space-between;
    padding-vertical: 5px;
    align-items: center;
`

const Promotion = styled.div``

const RemovePromotion = styled(Text)`
    color: ${red2};
    text-decoration: underline;
    margin-bottom: 10px;
`
const DeletePromotion = styled.div`
    cursor: pointer;
`

const Payment = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin: 15px 0 35px;
`

export const CartSummaryComponent = injectIntl(CartSummary)
